import React, { useState, useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import SensorSticker from "../SensorSticker";
import SensorStatusTag from "../SensorStatusTag";
import InstallSensorLocation from "../InstallSensorLocation";
import ToggleOnOff from "../ToggleOnOff";
import ButtonSpinner from "../Spinners/ButtonSpinner";
import theme from "../../Styles/theme.scss";

import classNames from "classnames";
import { getLastEventStatus } from "Services/Utils/newDeviceMonitoring/getLastEvent";
import {
  AugmentDeviceData,
  SensorStatus,
} from "Services/Utils/newDeviceMonitoring/types";
import { EditIcon } from "Styles/Icons";
import {
  getDevelcoSensorLEDStatus,
  postDevelcoSensorLEDStatus,
} from "@intelligentlilli/api-layer";
import {
  getPerSensorLEDCache,
  setPerSensorLEDCache,
} from "../../Services/Constants";
import { SERVER } from "../../Services/config";

interface SensorProps {
  device: AugmentDeviceData;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  sensorStatus: SensorStatus;
  isOffline: boolean;
  onSaveLocation?: (
    device: AugmentDeviceData,
    newValue: string
  ) => Promise<void>;
  devicestring?: string;
}

const Sensor = ({
  device,
  onClick,
  sensorStatus,
  isOffline,
  onSaveLocation,
  devicestring,
}: SensorProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Display sensorLocationafter a successful save without waiting for Redux state changes
  const [displayLocation, setDisplayLocation] = useState<string>(
    device.sensorLocation ?? ""
  );

  // Keep in sync when the augmented device data updates (e.g. after Redux update)
  useEffect(() => {
    if (!isEditing) {
      setDisplayLocation(device.sensorLocation ?? "");
    }
  }, [device.sensorLocation, isEditing]);

  const { lastEvent } = getLastEventStatus(device);

  // Show the edit button for non-hub sensors that have location options
  const isHub = device.label?.toUpperCase() === "H1";
  const canEditLocation =
    !isHub &&
    device.locationRequired !== false &&
    !!device.locationOptions?.length &&
    !!onSaveLocation;

  // -- LED toggle logic (motion/door sensors only) --
  const label = device.label?.toLowerCase() ?? "";
  const showLEDToggle =
    !!devicestring &&
    !isOffline &&
    (label.startsWith("m") || label.startsWith("d"));
  const sensorId =
    device.child_device_internal_id?.split("dev_")[1] ?? "";

  const [ledEnabled, setLedEnabled] = useState(false);
  const [ledLoading, setLedLoading] = useState(showLEDToggle);
  const [ledError, setLedError] = useState<string | null>(null);
  const [ledSuccess, setLedSuccess] = useState(false);
  const isTogglingRef = useRef(false);

  useEffect(() => {
    if (!showLEDToggle || !sensorId) return;

    const cached = getPerSensorLEDCache(devicestring!, sensorId);
    if (cached !== null) {
      setLedEnabled(cached);
      setLedLoading(false);
      return;
    }

    let cancelled = false;

    getDevelcoSensorLEDStatus(SERVER, devicestring!, sensorId, "web")
      .then((res) => {
        if (cancelled) return;
        if (
          res.ok &&
          res.data &&
          "data" in res.data
        ) {
          setLedEnabled(res.data.data === true);
        }
        setLedLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLedError("Could not get LED status. Refresh to try again");
        setLedLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLEDToggle, devicestring, sensorId]);

  const toggleLED = (newState: boolean) => {
    if (isTogglingRef.current || !devicestring || !sensorId) return;
    isTogglingRef.current = true;
    setLedLoading(true);
    setLedError(null);
    setLedSuccess(false);

    postDevelcoSensorLEDStatus(
      SERVER,
      devicestring,
      sensorId,
      { state: newState },
      "web"
    )
      .then((res) => {
        if (res.ok) {
          setLedEnabled(newState);
          setPerSensorLEDCache(devicestring, sensorId, newState);
          setLedSuccess(true);
        } else {
          setLedError("Could not update LED. Try again");
        }
        isTogglingRef.current = false;
        setLedLoading(false);
      })
      .catch(() => {
        setLedError("Could not update LED. Try again");
        isTogglingRef.current = false;
        setLedLoading(false);
      });
  };

  const handleLocationChange = async (location: { value: string }) => {
    if (!onSaveLocation || isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await onSaveLocation(device, location.value);
      setDisplayLocation(location.value);
      setIsEditing(false);
    } catch {
      setSaveError("Could not save location. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={classNames(styles.sensor, {
        [styles.hover]: sensorStatus.variant === "offline" && !isEditing,
        [styles.editing]: isEditing,
      })}
      onClick={!isEditing ? onClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.sensor_left}>
        <div className={styles.sensor_name_container}>
          <p className={styles.sensor_name}>
            {device?.displayName || device?.defaultName}
          </p>
          {!isEditing && displayLocation && displayLocation !== "other" ? (
            <p className={styles.location}>{displayLocation}</p>
          ) : null}
          {canEditLocation && !isEditing && (
            <button
              className={styles.edit_button}
              onClick={(e) => {
                e.stopPropagation();
                setSaveError(null);
                setIsEditing(true);
              }}
              aria-label="Edit sensor location"
            >
              <EditIcon style={{ width: 14, height: 14 }} />
              <span>Edit sensor location</span>
            </button>
          )}
        </div>
      </div>
      <div className={styles.sensor_right}>
        <div className={styles.device_group}>
          <img src={device?.img} className={styles.image} alt="sensor" />
          <SensorSticker label={device?.label} />
        </div>
      </div>

      {/* Bottom row: status on left, LED toggle on right */}
      <div className={styles.sensor_bottom_row}>
        <div className={styles.bottom_left}>
          <SensorStatusTag
            sensorStatus={sensorStatus}
            isHovered={isHovered && !isEditing}
          />
          {isOffline && lastEvent ? (
            <p className={styles.last_online}>Last online: {lastEvent}</p>
          ) : null}
        </div>
        {showLEDToggle && (
          <div className={styles.led_toggle_group}>
            {ledLoading ? (
              <ButtonSpinner />
            ) : (
              <>
                <span className={styles.led_label}>
                  {ledEnabled ? "LED on" : "LED off"}
                </span>
                <ToggleOnOff
                  isEnabled={ledEnabled}
                  onToggleOn={() => toggleLED(true)}
                  onToggleOff={() => toggleLED(false)}
                  backgroundColor={theme.primary3}
                  enabledLabel=""
                  disabledLabel=""
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* LED feedback messages */}
      {showLEDToggle && ledSuccess && (
        <p className={styles.led_message}>
          It can take up to 2 minutes for changes to take effect
        </p>
      )}
      {showLEDToggle && ledError && (
        <p className={styles.led_error}>{ledError}</p>
      )}

      {/* Edit location section */}
      {isEditing && (
        <div
          className={styles.edit_location_container}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.edit_location_header}>
            <p className={styles.edit_location_title}>Edit location</p>
            <button
              className={styles.cancel_button}
              onClick={() => {
                setIsEditing(false);
                setSaveError(null);
              }}
            >
              Cancel
            </button>
          </div>
          <InstallSensorLocation
            sensor={{
              name: device.location ?? device.child_device_internal_id,
              locationRequired: true,
              locationOptions: device.locationOptions ?? [],
              label: device.label?.toLowerCase(),
            }}
            isHub={false}
            showLocationError={false}
            onLocationChange={handleLocationChange}
            currentLocation={displayLocation}
          />
          {isSaving && (
            <p className={styles.saving_text}>Saving…</p>
          )}
          {saveError && (
            <p className={styles.save_error}>{saveError}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Sensor;
